import { createRouter, createWebHashHistory } from 'vue-router';
import { initPermission } from '@/utils/permission';
import { MessagePlugin } from 'tdesign-vue-next';
import { cancelAllPendingRequests } from '@/utils/request';

// 静态路由 - 只包含不需要权限的公开页面
const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: {
      title: '登录',
      requiresAuth: false
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/register/index.vue'),
    meta: {
      title: '注册',
      requiresAuth: false
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/terms/index.vue'),
    meta: {
      title: '服务条款',
      requiresAuth: false
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/privacy/index.vue'),
    meta: {
      title: '隐私政策',
      requiresAuth: false
    }
  }
];

const router = createRouter({
  history: createWebHashHistory(),
  routes: constantRoutes
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 取消所有未完成的请求
  if (from.path !== '/') {
    cancelAllPendingRequests();
  }
  
  const accessToken = localStorage.getItem('access_token');
  
  console.log('路由守卫:', to.path, '当前路由数量:', router.getRoutes().length);
  
  // 处理根路径 /
  if (to.path === '/') {
    if (accessToken) {
      // 有token，初始化权限后跳转首页
      const routeCount = router.getRoutes().length;
      if (routeCount <= constantRoutes.length) {
        console.log('初始化权限...');
        const initialized = initPermission();
        if (initialized) {
          console.log('权限初始化成功，跳转首页');
          next({ path: '/dashboard/home', replace: true });
        } else {
          console.error('权限初始化失败');
          next({ path: '/login' });
        }
      } else {
        next({ path: '/dashboard/home', replace: true });
      }
    } else {
      next({ path: '/login' });
    }
    return;
  }
  
  // 不需要认证的页面
  if (to.meta.requiresAuth === false) {
    // 如果是登录页且已有token
    if (to.path === '/login' && accessToken) {
      // 确保路由已初始化
      if (router.getRoutes().length <= constantRoutes.length) {
        initPermission();
      }
      next({ path: '/dashboard/home' });
    } else {
      next();
    }
    return;
  }
  
  // 需要认证的页面
  if (!accessToken) {
    next({ path: '/login', query: { redirect: to.fullPath } });
    return;
  }
  
  // 检查路由是否已初始化
  const routeCount = router.getRoutes().length;
  if (routeCount <= constantRoutes.length) {
    console.log('路由未初始化，开始初始化...');
    const initialized = initPermission();
    if (initialized) {
      console.log('权限初始化成功，重新导航到:', to.fullPath);
      // 使用 nextTick 确保路由已添加
      await new Promise(resolve => setTimeout(resolve, 0));
      next({ ...to, replace: true });
    } else {
      console.error('权限初始化失败');
      MessagePlugin.error('权限加载失败，请重新登录');
      next({ path: '/login' });
    }
    return;
  }
  
  // 路由已初始化，检查路由是否存在
  if (to.matched.length === 0) {
    console.warn('路由不存在:', to.path, '尝试重新初始化');
    // 清除初始化标记，重新初始化
    localStorage.removeItem('routes_initialized');
    const reInitialized = initPermission();
    if (reInitialized) {
      await new Promise(resolve => setTimeout(resolve, 0));
      next({ path: to.fullPath, replace: true });
    } else {
      MessagePlugin.error('页面不存在');
      next({ path: '/dashboard/home', replace: true });
    }
    return;
  }
  
  // 检查页面权限
  try {
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
    const permissions = userInfo?.permissions?.pages || [];
    
    // Home 和 Dashboard 页面不需要额外权限检查
    if (to.name === 'Home' || to.name === 'Dashboard') {
      next();
      return;
    }
    
    // 检查用户是否有访问该页面的权限
    const hasPermission = permissions.some(page => {
      if (page.code === to.name) return true;
      if (page.children && page.children.length > 0) {
        return page.children.some(child => child.code === to.name);
      }
      return false;
    });
    
    if (!hasPermission) {
      MessagePlugin.warning('您没有访问该页面的权限');
      next({ path: '/dashboard/home', replace: true });
      return;
    }
    
    next();
  } catch (error) {
    console.error('权限检查失败:', error);
    MessagePlugin.error('权限检查失败');
    next({ path: '/dashboard/home', replace: true });
  }
});

export default router;
